import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RetryFailedEntitiesService } from './retry-failed-entities.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';


@Component({
    selector: 'gtw-retry-failed-entities',
    templateUrl: './retry-failed-entities.component.html',
    styleUrls: ['./retry-failed-entities.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RetryFailedEntitiesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Data inputs
  rowData: any = {};
  filterParams: any = {};
  GENERAL_CONFIG: any = {};
  userSettings: any = {};
  serverConfig: any = {};

  // Component state
  loading = false;
  entities: any[] = [];
  status = '';
  userInitiatedRequest = false;
  currentTheme = 'theme-black';
  private initialized = false;

  constructor(
    private retryFailedEntitiesService: RetryFailedEntitiesService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.initialized = false;
        this.applyWorkspaceContext();
        this.init();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.initialized = false;
        this.applyWorkspaceContext();
        this.init();
        this.cdr.markForCheck();
      })
    );
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.userSettings = header.userSettingsData || {};
    this.serverConfig = header.serverConfigData || {};
    this.currentTheme = this.userSettings?.user?.selected_theme || 'theme-black';
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? (rawRow[0] && typeof rawRow[0] === 'object' ? rawRow[0] : {}) : (rawRow || {});
    this.cdr.markForCheck();
  }

  init() {
    try {
      // Prevent duplicate initialization
      if (this.initialized) {
        return;
      }

      if (!this.rowData || Object.keys(this.rowData).length === 0) {
        return;
      }

      if (!this.filterParams || Object.keys(this.filterParams).length === 0) {
        return;
      }

      // Mark as initialized
      this.initialized = true;

      // Get status from rowData
      this.status = this.rowData.STATUS || '';

      // Get taxYear from filterParams
      const taxYear = this.filterParams.tax_year || this.filterParams.TAX_YEAR;

      // Check if the request is in progress
      if (this.status === 'In Progress') {
        this.userInitiatedRequest = false;
        this.loading = false;
        this.cdr.detectChanges();
        return;
      }

      // Load failed entities for other statuses
      this.userInitiatedRequest = false;
      this.loading = true;
      this.cdr.detectChanges();

      this.retryFailedEntitiesService.getFailedEntities(
        this.GENERAL_CONFIG.pdf_engine_api_url,
        taxYear,
        this.rowData.REQUEST_ID
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          this.loading = false;
          // Match AngularJS response structure: response.data.entities (not response.entities)
          this.entities = response.entities || [];
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error loading failed entities:', error);
          this.loading = false;
          
          // Don't show error message if taxYear is missing (data issue, not user error)
          if (taxYear) {
            this._snackBar.open('Error occurred while loading entities. Please try again.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
          this.cdr.detectChanges();
        }
      );

    } catch (e) {
      console.error('Error in retry-failed-entities init:', e);
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  retryBulkExport() {
    this.loading = true;
    this.cdr.detectChanges();

    const payload = {
      entities: "All",
      forms: "All"
    };

    // Get values from rowData (matching AngularJS behavior)
    const taxYear = this.rowData.TAX_YEAR;
    const scenario = this.rowData.SCENARIO;
    const jcdKey = this.rowData.JCD_KEY || '250';
    
    // Get values from rowData
    const taxPayer = this.rowData.TAX_PAYER || '';
    const requestType = this.rowData.REQUEST_TYPE;
    const format = this.rowData.FORMAT;
    const contentType = this.rowData.CONTENT_TYPE;
    const returnType = this.rowData.RETURN_TYPE;
    const businessType = this.rowData.BUSINESS_TYPE || '';
    const groupKey = this.rowData.GROUP_KEY || '';
    const requestId = this.rowData.REQUEST_ID;
    const requestedBy = this.rowData.REQUESTED_BY;
    const groupName = this.rowData.GROUP_NAME || '';

    this.retryFailedEntitiesService.pdfsBulkExport(
      this.GENERAL_CONFIG.pdf_engine_api_url,
      taxYear,
      scenario,
      taxPayer,
      requestType,
      format,
      (contentType === "STATE") ? "STATE" : 'EFILE',
      returnType,
      businessType,
      jcdKey,
      groupKey,
      requestId,
      requestedBy,
      (contentType === "STATE") ? groupName : taxPayer,
      payload,
      ''  // formType (empty string to match AngularJS default)
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        this._snackBar.open('Your Bulk Export Request has been Initiated!', 'Close', {
          duration: 4000,
          panelClass: ['success-snackbar']
        });
        this.cdr.detectChanges();
        
        // Emit refresh event for AngularJS
        this.requestGridUpdate.emit('refresh');
      },
      (error: any) => {
        console.error('Error retrying bulk export:', error);
        this.loading = false;
        this._snackBar.open('Something went wrong while sending Bulk Request!', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
        this.cdr.detectChanges();
        
        // Still close the modal even on error
        this.requestGridUpdate.emit('refresh');
      }
    );
  }

  close() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }
}
